import {
    StarIcon,
} from "lucide-react";

interface SocialProofIndicatorProps {
    variant?: "companies" | "statistics" | "avatars" | "testimonial";
    className?: string;
}

export default function SocialProofIndicator({
    variant = "companies",
    className = "",
}: SocialProofIndicatorProps) {
    // Mock company data
    const companies = [
        {
            name: "Google",
            logo: "https://assets.polymet.ai/advisory-fuchsia-891542",
        },
        {
            name: "Microsoft",
            logo: "https://assets.polymet.ai/smiling-yellow-445964",
        },
        { name: "Apple", logo: "https://assets.polymet.ai/chief-harlequin-590492" },
        { name: "Meta", logo: "https://assets.polymet.ai/domestic-emerald-941714" },
        {
            name: "Netflix",
            logo: "https://assets.polymet.ai/soviet-moccasin-579547",
        },
        {
            name: "Amazon",
            logo: "https://assets.polymet.ai/domestic-emerald-941714",
        },
        { name: "DBS", logo: "https://assets.polymet.ai/shy-gray-045727" },
    ];

    // Rearranged companies for display - Meta in middle, DBS and Google at right
    const displayCompanies = [
        companies[1], // Microsoft
        companies[2], // Apple
        companies[3], // Meta (middle position)
        companies[4], // Netflix
        companies[6], // DBS
        companies[0], // Google (added to the right)
    ];

    // Mock user avatars
    const recentUsers = [
        "https://github.com/yusufhilmi.png",
        "https://github.com/kdrnp.png",
        "https://github.com/yahyabedirhan.png",
        "https://github.com/denizbuyuktas.png",
        "https://github.com/shoaibux1.png",
    ];

    if (variant === "companies") {
        return (
            <div className={`${className}`}>
                <div className="text-center mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Trusted by candidates at
                    </p>
                </div>
                {/* Desktop layout - single row */}
                <div className="hidden sm:flex items-center justify-center gap-6 opacity-60">
                    {displayCompanies.map((company, index) => (
                        <div key={index} className="flex-shrink-0">
                            <img
                                src={company.logo}
                                alt={company.name}
                                className="h-5 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-200"
                                onError={(e) => {
                                    // Fallback to text if logo fails to load
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `<span class="text-xs font-medium text-gray-400">${company.name}</span>`;
                                    }
                                }}
                            />
                        </div>
                    ))}
                    <span className="text-xs text-gray-400">+50 more</span>
                </div>

                {/* Mobile layout - two rows */}
                <div className="sm:hidden opacity-60">
                    <div className="grid grid-cols-3 gap-4 justify-items-center mb-4">
                        {displayCompanies.slice(0, 3).map((company, index) => (
                            <div key={index} className="flex-shrink-0">
                                <img
                                    src={company.logo}
                                    alt={company.name}
                                    className="h-4 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-200"
                                    onError={(e) => {
                                        // Fallback to text if logo fails to load
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = "none";
                                        const parent = target.parentElement;
                                        if (parent) {
                                            parent.innerHTML = `<span class="text-xs font-medium text-gray-400">${company.name}</span>`;
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-4 gap-3 justify-items-center items-center">
                        {displayCompanies.slice(3).map((company, index) => (
                            <div key={index + 3} className="flex-shrink-0">
                                <img
                                    src={company.logo}
                                    alt={company.name}
                                    className="h-4 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-200"
                                    onError={(e) => {
                                        // Fallback to text if logo fails to load
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = "none";
                                        const parent = target.parentElement;
                                        if (parent) {
                                            parent.innerHTML = `<span class="text-xs font-medium text-gray-400">${company.name}</span>`;
                                        }
                                    }}
                                />
                            </div>
                        ))}
                        <span className="text-xs text-gray-400">+50 more</span>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === "statistics") {
        return (
            <div className={`${className}`}>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-8">
                        <div className="flex items-center justify-center gap-3 flex-1">
                            <div className="flex -space-x-2">
                                {recentUsers.slice(0, 3).map((avatar, index) => (
                                    <img
                                        key={index}
                                        src={avatar}
                                        alt={`User ${index + 1}`}
                                        className="w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 object-cover"
                                        style={{
                                            marginLeft: index === 0 ? "0" : "-6px",
                                            border: "1px solid rgb(228, 228, 231)",
                                        }}
                                    />
                                ))}
                            </div>
                            <div className="text-left">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    completed by 300+ talents across Asia
                                </p>
                            </div>
                        </div>
                        <div className="w-px h-8 bg-gray-200 dark:bg-gray-600 flex-shrink-0"></div>
                        <div className="flex flex-col items-center gap-2 flex-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Trusted by candidates at
                            </p>
                            <div className="flex items-center justify-center gap-2 opacity-60">
                                {companies.slice(0, 3).map((company, index) => (
                                    <div key={index} className="flex-shrink-0">
                                        <img
                                            src={company.logo}
                                            alt={company.name}
                                            className="h-4 w-4 object-contain grayscale hover:grayscale-0 transition-all duration-200"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = "none";
                                                const parent = target.parentElement;
                                                if (parent) {
                                                    parent.innerHTML = `<span class="text-xs font-medium text-gray-400">${company.name}</span>`;
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                                <span className="text-xs text-gray-400">+500 more</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === "avatars") {
        return (
            <div className={`${className}`}>
                <div className="flex items-center justify-center gap-3">
                    <div className="flex -space-x-2">
                        {recentUsers.slice(0, 4).map((avatar, index) => (
                            <img
                                key={index}
                                src={avatar}
                                alt={`User ${index + 1}`}
                                className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 object-cover"
                                style={{
                                    marginLeft: "-8px",
                                    border: "1px solid rgb(228, 228, 231)",
                                }}
                            />
                        ))}
                    </div>
                    <div className="text-left">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            247 people completed this today
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Join thousands of successful candidates.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (variant === "testimonial") {
        return (
            <div className={`${className}`}>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <img
                                src="https://github.com/yusufhilmi.png"
                                alt="Sarah Chen"
                                className="w-8 h-8 rounded-full object-cover"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 mb-1">
                                {[...Array(5)].map((_, i) => (
                                    <StarIcon
                                        key={i}
                                        className="h-3 w-3 fill-yellow-400 text-yellow-400"
                                    />
                                ))}
                            </div>
                            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                                "The AI interview helped me showcase skills that weren't obvious
                                from my resume. Got the job!"
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                    Sarah C.
                                </span>
                                <span className="text-xs text-gray-400">•</span>
                                <img
                                    src="https://logo.clearbit.com/google.com"
                                    alt="Google"
                                    className="h-3 w-3 object-contain opacity-60"
                                />

                                <span className="text-xs text-gray-400">Software Engineer</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
